import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaBars, FaTimes } from 'react-icons/fa';

const Nav = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: ${props => props.scrolled ? 'blur(10px)' : 'blur(8px)'};
  box-shadow: ${props => props.scrolled ? '0 2px 20px rgba(0, 0, 0, 0.08)' : '0 1px 10px rgba(0, 0, 0, 0.05)'};
  transition: all 0.3s ease;
  padding: 1.25rem 0;
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.5rem;
  font-weight: 800;
  color: #1a1a1a;
  
  svg {
    height: 40px;
    width: auto;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled.a`
  color: #1a1a1a;
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 3px;
    background: #0891b2;
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: #0891b2;
    
    &::after {
      width: 100%;
    }
  }
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(8, 145, 178, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(8, 145, 178, 0.3);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  color: #1a1a1a;
  font-size: 1.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  z-index: 999;
  padding: 2rem;
  
  a {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a1a1a;
    transition: color 0.3s ease;
    
    &:hover {
      color: #0891b2;
    }
  }
  
  button {
    margin-top: 1rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: none;
  color: #1a1a1a;
  font-size: 1.5rem;
  z-index: 1001;
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
`;

const LighthouseLogo = () => (
  <svg viewBox="0 0 200 50" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Lighthouse with integrated waves */}
    <g>
      {/* Lighthouse structure */}
      <path d="M25 8 L22 20 L28 20 Z" fill="#0891b2"/>
      <rect x="22" y="20" width="6" height="20" fill="#0891b2"/>
      
      {/* Light beam as waves */}
      <path d="M30 15 Q40 12 50 15 T70 15" stroke="#06b6d4" strokeWidth="2" fill="none" opacity="0.8"/>
      <path d="M30 18 Q40 15 50 18 T70 18" stroke="#0891b2" strokeWidth="2" fill="none" opacity="0.6"/>
      <path d="M30 21 Q40 18 50 21 T70 21" stroke="#0e7490" strokeWidth="2" fill="none" opacity="0.4"/>
      
      {/* Base */}
      <path d="M20 40 L30 40 L32 45 L18 45 Z" fill="#164e63"/>
      
      {/* Light at top */}
      <circle cx="25" cy="8" r="2" fill="#fbbf24"/>
      <circle cx="25" cy="8" r="4" fill="#fbbf24" opacity="0.3"/>
    </g>
    
    {/* Text */}
    <text x="80" y="32" fontFamily="Arial, sans-serif" fontSize="28" fontWeight="700" fill="#1a1a1a">Faroflow</text>
  </svg>
);

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <Nav
        scrolled={scrolled}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <NavContainer>
          <Logo>
            <LighthouseLogo />
          </Logo>
          
          <NavLinks>
            <NavLink href="#features">Features</NavLink>
            <NavLink href="#value">Value</NavLink>
            <NavLink href="#use-cases">Use Cases</NavLink>
            <NavLink href="#how-it-works">How It Works</NavLink>
            <CTAButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Get Started
            </CTAButton>
          </NavLinks>
          
          <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </MobileMenuButton>
        </NavContainer>
      </Nav>
      
      {mobileMenuOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileMenuOpen(false)}
          />
          <MobileMenu
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween' }}
          >
            <CloseButton onClick={() => setMobileMenuOpen(false)}>
              <FaTimes />
            </CloseButton>
            <NavLink href="#features" onClick={() => setMobileMenuOpen(false)}>Features</NavLink>
            <NavLink href="#value" onClick={() => setMobileMenuOpen(false)}>Value</NavLink>
            <NavLink href="#use-cases" onClick={() => setMobileMenuOpen(false)}>Use Cases</NavLink>
            <NavLink href="#how-it-works" onClick={() => setMobileMenuOpen(false)}>How It Works</NavLink>
            <CTAButton
              onClick={() => {
                setMobileMenuOpen(false);
                document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Get Started
            </CTAButton>
          </MobileMenu>
        </>
      )}
    </>
  );
};

export default Navigation; 